/* Impulse Padel: show only media matching the selected colour variant. */
(() => {
  const COLOR_OPTION_NAMES = ['couleur', 'color', 'colour', 'coloris'];
  const MEDIA_SELECTOR = [
    'slideshow-slide',
    '.product-media-container',
    '.slideshow-controls__thumbnail',
    '.dialog-thumbnails-list__thumbnail',
    '.dialog-zoomed-gallery > li',
  ].join(',');

  const COLOR_SYNONYMS = {
    blanc: ['blanc', 'blanche', 'white'],
    bleu: ['bleu', 'bleue', 'blue'],
    creme: ['creme', 'cream', 'ecru', 'ivoire', 'ivory'],
    ecru: ['ecru', 'creme', 'cream', 'ivoire', 'ivory'],
    gris: ['gris', 'grise', 'gray', 'grey'],
    ivoire: ['ivoire', 'ivory', 'creme', 'cream', 'ecru'],
    jaune: ['jaune', 'yellow'],
    noir: ['noir', 'noire', 'black'],
    orange: ['orange'],
    or: ['or', 'gold', 'dore', 'doree'],
    rose: ['rose', 'pink'],
    rouge: ['rouge', 'red'],
    vert: ['vert', 'verte', 'green'],
    violet: ['violet', 'violette', 'purple'],
  };

  const normalize = (value) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  const words = (value) => normalize(value).split(/\s+/).filter(Boolean);

  const isColorOption = (value) => {
    const optionName = normalize(value);
    return COLOR_OPTION_NAMES.some((name) => optionName.includes(name));
  };

  const getSelectedColor = (picker) => {
    for (const fieldset of picker.querySelectorAll('fieldset')) {
      const optionName =
        fieldset.dataset.optionName || fieldset.getAttribute('aria-label') || fieldset.querySelector('legend')?.textContent;

      if (!isColorOption(optionName)) continue;

      const selected = fieldset.querySelector('input:checked');
      if (selected instanceof HTMLInputElement) {
        return selected.value || selected.getAttribute('aria-label') || selected.closest('label')?.textContent || '';
      }
    }

    for (const select of picker.querySelectorAll('select')) {
      const optionName =
        select.name ||
        select.getAttribute('aria-label') ||
        select.closest('.variant-option')?.querySelector('label')?.textContent ||
        '';

      if (!isColorOption(optionName)) continue;

      return select.options[select.selectedIndex]?.value || select.value || '';
    }

    return '';
  };

  const getMediaWords = (item) => {
    const values = [item.getAttribute('aria-label'), item.getAttribute('id'), item.getAttribute('data-media-id')];

    item.querySelectorAll('img, source, video').forEach((node) => {
      values.push(node.getAttribute('alt'));
      values.push(node.getAttribute('src'));
      values.push(node.getAttribute('srcset'));
      values.push(node.getAttribute('poster'));
    });

    return new Set(words(values.filter(Boolean).join(' ')));
  };

  const mediaMatchesColor = (item, selectedColor) => {
    const mediaWords = getMediaWords(item);
    const colorWords = words(selectedColor).filter((word) => !['et', 'and', 'avec'].includes(word));

    if (!colorWords.length || !mediaWords.size) return false;

    return colorWords.every((word) => {
      const alternatives = COLOR_SYNONYMS[word] || [word];
      return alternatives.some((alternative) => mediaWords.has(alternative));
    });
  };

  const showElement = (element) => {
    element.hidden = false;
    element.style.removeProperty('display');
    element.removeAttribute('aria-hidden');
  };

  const hideElement = (element) => {
    element.hidden = true;
    element.style.setProperty('display', 'none', 'important');
    element.setAttribute('aria-hidden', 'true');
  };

  const rememberControl = (control) => {
    if (!control.hasAttribute('data-impulse-original-click')) {
      control.setAttribute('data-impulse-original-click', control.getAttribute('on:click') || '');
      control.setAttribute('data-impulse-original-label', control.getAttribute('aria-label') || '');
      control.setAttribute('data-impulse-original-text', control.textContent || '');
    }
  };

  const restoreControl = (control) => {
    const originalClick = control.getAttribute('data-impulse-original-click');
    const originalLabel = control.getAttribute('data-impulse-original-label');
    const originalText = control.getAttribute('data-impulse-original-text');

    if (originalClick !== null) {
      if (originalClick) control.setAttribute('on:click', originalClick);
      else control.removeAttribute('on:click');
    }
    if (originalLabel !== null) {
      if (originalLabel) control.setAttribute('aria-label', originalLabel);
      else control.removeAttribute('aria-label');
    }
    if (originalText !== null) control.textContent = originalText;
  };

  const resetGallery = (gallery) => {
    gallery.querySelectorAll(MEDIA_SELECTOR).forEach((element) => showElement(element));

    gallery.querySelectorAll('button[ref="dots[]"]').forEach((button) => {
      showElement(button.closest('li') || button);
      restoreControl(button);
    });

    gallery.querySelectorAll('button[ref="thumbnails[]"]').forEach((button) => {
      showElement(button);
      restoreControl(button);
    });

    gallery.querySelectorAll('[data-impulse-filter-controls]').forEach((controls) => {
      controls.hidden = controls.getAttribute('data-impulse-original-hidden') === 'true';
      controls.removeAttribute('data-impulse-filter-controls');
      controls.removeAttribute('data-impulse-original-hidden');
    });

    gallery.removeAttribute('data-impulse-color-media-filtered');
  };

  const updateIndexedControls = (gallery, selector, visibleIndexes, visibleCount) => {
    const controls = Array.from(gallery.querySelectorAll(selector));
    const groups = new Map();

    controls.forEach((control) => {
      const group = control.closest('ol, scroll-hint, .slideshow-controls__thumbnails') || control.parentElement;
      if (!group) return;
      if (!groups.has(group)) groups.set(group, []);
      groups.get(group).push(control);
    });

    groups.forEach((groupControls) => {
      groupControls.forEach((control, originalIndex) => {
        rememberControl(control);
        const visibleIndex = visibleIndexes.indexOf(originalIndex);
        const target = selector.includes('dots') ? control.closest('li') || control : control;

        if (visibleIndex === -1) {
          hideElement(target);
          return;
        }

        showElement(target);
        control.setAttribute('on:click', `/select/${visibleIndex}`);

        if (selector.includes('dots')) {
          control.textContent = String(visibleIndex + 1);
          control.setAttribute('aria-label', `Image ${visibleIndex + 1} sur ${visibleCount}`);
        }
      });
    });
  };

  const updateControlsVisibility = (gallery, visibleCount) => {
    gallery.querySelectorAll('slideshow-controls, slideshow-arrows').forEach((controls) => {
      if (!controls.hasAttribute('data-impulse-filter-controls')) {
        controls.setAttribute('data-impulse-original-hidden', String(controls.hidden));
      }
      controls.setAttribute('data-impulse-filter-controls', '');
      controls.hidden = visibleCount <= 1;
    });

    gallery.querySelectorAll('.slideshow-controls__counter').forEach((counter) => {
      const current = counter.querySelector('[ref="current"]');
      if (current) current.textContent = '1';

      const textNodes = Array.from(counter.childNodes).filter((node) => node.nodeType === Node.TEXT_NODE);
      const totalNode = textNodes.at(-1);
      if (totalNode) totalNode.nodeValue = String(visibleCount);
    });
  };

  const filterGallery = (picker) => {
    const productInformation = picker.closest('.product-information');
    const gallery = productInformation?.querySelector('media-gallery');
    if (!gallery) return;

    resetGallery(gallery);

    const selectedColor = getSelectedColor(picker);
    if (!selectedColor) return;

    const slideshow = gallery.querySelector('slideshow-component');
    const slides = Array.from(slideshow?.querySelectorAll('slideshow-slides > slideshow-slide') || []);
    if (!slides.length) return;

    const visibleIndexes = slides.reduce((indexes, slide, index) => {
      if (mediaMatchesColor(slide, selectedColor)) indexes.push(index);
      return indexes;
    }, []);

    if (!visibleIndexes.length) return;

    slides.forEach((slide, index) => {
      if (visibleIndexes.includes(index)) showElement(slide);
      else hideElement(slide);
    });

    gallery.querySelectorAll('.media-gallery__grid > li, .dialog-zoomed-gallery > li').forEach((item) => {
      if (mediaMatchesColor(item, selectedColor)) showElement(item);
      else hideElement(item);
    });

    gallery.querySelectorAll('.slideshow-controls__thumbnail, .dialog-thumbnails-list__thumbnail').forEach((thumbnail) => {
      if (mediaMatchesColor(thumbnail, selectedColor)) showElement(thumbnail);
      else hideElement(thumbnail);
    });

    updateIndexedControls(gallery, 'button[ref="dots[]"]', visibleIndexes, visibleIndexes.length);
    updateIndexedControls(gallery, 'button[ref="thumbnails[]"]', visibleIndexes, visibleIndexes.length);
    updateControlsVisibility(gallery, visibleIndexes.length);

    gallery.setAttribute('data-impulse-color-media-filtered', normalize(selectedColor));

    const firstVisible = slides[visibleIndexes[0]];
    const scroller = slideshow?.querySelector('slideshow-slides');
    if (firstVisible && scroller) {
      scroller.scrollTo({ left: firstVisible.offsetLeft, behavior: 'auto' });
      slideshow.select?.(0, undefined, { animate: false });
    }
  };

  const filterAll = () => {
    document.querySelectorAll('main variant-picker').forEach((picker) => filterGallery(picker));
  };

  const scheduleFilter = () => {
    [0, 80, 180, 400].forEach((delay) => window.setTimeout(filterAll, delay));
  };

  customElements.whenDefined('variant-picker').then(() => {
    const VariantPicker = customElements.get('variant-picker');

    if (VariantPicker?.prototype?.filterProductMediaBySelectedVariant) {
      VariantPicker.prototype.filterProductMediaBySelectedVariant = () => {};
    }

    scheduleFilter();
  });

  document.addEventListener('change', (event) => {
    if (event.target instanceof Element && event.target.closest('variant-picker')) scheduleFilter();
  });

  document.addEventListener('shopify:section:load', scheduleFilter);
  window.addEventListener('popstate', scheduleFilter);
})();
